import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  type Table as TanstackTable,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import type { ReactNode } from 'react';
import * as React from 'react';
import { cn } from '../../lib/utils.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';
import { DataTableSkeleton } from './data-table-skeleton.js';

/**
 * Server-driven state. Supply this and the table stops paginating/sorting locally and
 * simply renders what it is given — the shape `useListQuery` produces.
 */
export interface DataTableServerState {
  pageCount: number;
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  /**
   * Control column filters too. Without these the toolbar's facet filters write into
   * TanStack's own state, which a server-driven table never reads — so clicking a facet
   * would appear to do nothing.
   */
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
}

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  /** Omit for a client-side table; supply to drive it from the server. */
  server?: DataTableServerState;
  isLoading?: boolean;
  /** Shown in place of the body when there are no rows and nothing is loading. */
  emptyState?: ReactNode;
  onRowClick?: (row: TData) => void;
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  /** Stable row identity. Required for selection to survive re-fetches. */
  getRowId?: (row: TData, index: number) => string;
  /** Render a toolbar and/or pagination around the table, given the table instance. */
  toolbar?: (table: TanstackTable<TData>) => ReactNode;
  footer?: (table: TanstackTable<TData>) => ReactNode;
  className?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  server,
  isLoading = false,
  emptyState,
  onRowClick,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  getRowId,
  toolbar,
  footer,
  className,
}: DataTableProps<TData, TValue>) {
  const [localSorting, setLocalSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [localSelection, setLocalSelection] = React.useState<RowSelectionState>({});
  const [localPagination, setLocalPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const isServer = !!server;

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting: server?.sorting ?? localSorting,
      columnFilters: server?.columnFilters ?? columnFilters,
      columnVisibility,
      rowSelection: rowSelection ?? localSelection,
      pagination: server?.pagination ?? localPagination,
    },
    ...(getRowId ? { getRowId } : {}),
    enableRowSelection,
    // With server state the row models must not re-slice or re-order: the server already
    // returned exactly one page in the requested order.
    manualPagination: isServer,
    manualSorting: isServer,
    manualFiltering: isServer,
    ...(isServer ? { pageCount: server.pageCount } : {}),
    onSortingChange: server?.onSortingChange ?? setLocalSorting,
    onPaginationChange: server?.onPaginationChange ?? setLocalPagination,
    onRowSelectionChange: onRowSelectionChange ?? setLocalSelection,
    onColumnFiltersChange: server?.onColumnFiltersChange ?? setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    ...(isServer
      ? {}
      : {
          getSortedRowModel: getSortedRowModel(),
          getFilteredRowModel: getFilteredRowModel(),
          getPaginationRowModel: getPaginationRowModel(),
        }),
  });

  const rows = table.getRowModel().rows;
  const columnCount = table.getAllLeafColumns().length;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {toolbar?.(table)}

      {/* Wide tables scroll inside their own container so the page body never does. */}
      <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <DataTableSkeleton
                columnCount={columnCount}
                rowCount={table.getState().pagination.pageSize}
              />
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="h-48 p-0">
                  {emptyState ?? (
                    <p className="py-12 text-center text-sm text-muted-foreground">No results.</p>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row: Row<TData>) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  {...(onRowClick
                    ? {
                        // Focusable so the row's action is reachable by keyboard, but the
                        // implicit `row` role is left alone: overriding it with `button`
                        // drops the row out of the table's structure, and a screen reader
                        // then loses the column headers for every cell inside it.
                        tabIndex: 0,
                        onClick: () => onRowClick(row.original),
                        onKeyDown: (event: React.KeyboardEvent) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onRowClick(row.original);
                          }
                        },
                        className:
                          'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                      }
                    : {})}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {footer?.(table)}
    </div>
  );
}
